import type { WordTimestamp } from '@/types/simulation-results';

export const ANALYSIS_BREAKDOWN_SCHEMA_VERSION = 'answer_analysis.v1';
export const ANALYSIS_RUBRIC_VERSION = '2026-06-08';

export type FeedbackKey =
  | 'task_fulfillment'
  | 'grammar'
  | 'spelling'
  | 'structure'
  | 'pronunciation'
  | 'fluency'
  | 'vocabulary'
  | 'register'
  | 'style'
  | 'interaction';

export interface CriterionBreakdown {
  readonly score?: number;
  readonly weight?: number;
  readonly feedback?: string;
}

export interface AnalysisBreakdown {
  schema_version: string;
  source: 'ai' | 'manual' | 'legacy' | string;
  task_type?: string | null;
  rubric_version?: string | null;
  final_score?: number;
  points?: number;
  is_correct?: boolean | null;
  criteria?: Record<string, CriterionBreakdown>;
  feedback?: {
    summary?: string;
    strengths?: string[];
    improvements?: string[];
    corrected_version?: string;
    task_fulfillment?: { text?: string };
    grammar?: { text?: string; errors?: unknown[] };
    spelling?: { text?: string; typos?: unknown[] };
    structure?: { text?: string };
    pronunciation?: { text?: string; errors?: unknown[] };
    fluency?: { text?: string; issues?: unknown[] };
    vocabulary?: { text?: string };
    register?: { text?: string };
    style?: { text?: string };
    interaction?: { text?: string };
  };
  transcription?: {
    text?: string;
    word_timestamps?: WordTimestamp[];
  };
  diagnostic_tags?: string[];
}

type AnalysisInput = AnalysisBreakdown | { analysis_breakdown?: unknown } | null | undefined;

const toText = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean).join('\n');
  }
  return String(value).trim();
};

const feedbackBlockText = (
  feedback: AnalysisBreakdown['feedback'],
  key: string,
): string => {
  const block = feedback?.[key as keyof NonNullable<AnalysisBreakdown['feedback']>];
  if (block && typeof block === 'object' && 'text' in block) {
    return toText((block as { text?: unknown }).text);
  }
  return toText(block);
};

// Criterion card feedback comes only from the AI's real, answer-specific
// analysis (the criterion entry or its matching feedback block). There is no
// score-based fallback: a weighted criterion without real feedback stays empty
// so the gap is visible instead of being masked by generic boilerplate.
const resolveCriterionFeedback = (
  criterion: string,
  entry: CriterionBreakdown,
  feedback: AnalysisBreakdown['feedback'],
): string => {
  const existing = toText(entry.feedback);
  if (existing) return existing;
  return feedbackBlockText(feedback, criterion);
};

export const enrichAnalysisBreakdown = (
  breakdown: AnalysisBreakdown,
): AnalysisBreakdown => {
  const enriched: AnalysisBreakdown = {
    ...breakdown,
    feedback: { ...breakdown.feedback },
    criteria: breakdown.criteria ? { ...breakdown.criteria } : {},
  };

  if (!enriched.feedback?.task_fulfillment) {
    enriched.feedback = { ...enriched.feedback, task_fulfillment: { text: '' } };
  }

  const criteria = enriched.criteria ?? {};
  for (const [criterion, rawEntry] of Object.entries(criteria)) {
    if (!rawEntry || typeof rawEntry !== 'object') continue;
    const entry = { ...rawEntry };
    const resolved = resolveCriterionFeedback(
      criterion,
      entry,
      enriched.feedback,
    );
    if (resolved) {
      entry.feedback = resolved;
      criteria[criterion] = entry;
      if (
        criterion === 'task_fulfillment' &&
        !feedbackBlockText(enriched.feedback, 'task_fulfillment')
      ) {
        enriched.feedback = {
          ...enriched.feedback,
          task_fulfillment: { text: resolved },
        };
      }
    }
  }
  enriched.criteria = criteria;
  return enriched;
};

const parseAnalysisBreakdownRaw = (value: unknown): AnalysisBreakdown | null => {
  const raw =
    value &&
    typeof value === 'object' &&
    'analysis_breakdown' in value
      ? (value as { analysis_breakdown?: unknown }).analysis_breakdown
      : value;

  if (!raw) return null;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as AnalysisBreakdown) : null;
    } catch {
      return null;
    }
  }
  return typeof raw === 'object' ? (raw as AnalysisBreakdown) : null;
};

export const normalizeAnalysisBreakdown = (
  value: unknown,
): AnalysisBreakdown | null => {
  const parsed = parseAnalysisBreakdownRaw(value);
  if (!parsed) return null;
  return enrichAnalysisBreakdown(parsed);
};

export const getFeedbackText = (input: AnalysisInput, key: FeedbackKey): string => {
  const breakdown = normalizeAnalysisBreakdown(input);
  const feedback = breakdown?.feedback?.[key];
  if (!feedback) return '';
  if (typeof feedback === 'object' && 'text' in feedback) {
    return toText((feedback as { text?: unknown }).text);
  }
  return toText(feedback);
};

export const getCorrectedVersion = (input: AnalysisInput): string => {
  return toText(normalizeAnalysisBreakdown(input)?.feedback?.corrected_version);
};

export const getFeedbackSummary = (input: AnalysisInput): string => {
  return toText(normalizeAnalysisBreakdown(input)?.feedback?.summary);
};

export const getStrengths = (input: AnalysisInput): string[] => {
  const value = normalizeAnalysisBreakdown(input)?.feedback?.strengths;
  return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
};

export const getImprovements = (input: AnalysisInput): string[] => {
  const value = normalizeAnalysisBreakdown(input)?.feedback?.improvements;
  return Array.isArray(value) ? value.map(String).filter(Boolean) : [];
};

export const getWordTimestamps = (input: AnalysisInput): WordTimestamp[] => {
  const timestamps = normalizeAnalysisBreakdown(input)?.transcription?.word_timestamps;
  return Array.isArray(timestamps) ? timestamps : [];
};

export const getCriterionScore = (
  input: AnalysisInput,
  criterion: string,
): number | null => {
  const score = normalizeAnalysisBreakdown(input)?.criteria?.[criterion]?.score;
  return typeof score === 'number' && Number.isFinite(score) ? score : null;
};

export const getCriterionWeight = (
  input: AnalysisInput,
  criterion: string,
): number | null => {
  const weight = normalizeAnalysisBreakdown(input)?.criteria?.[criterion]?.weight;
  return typeof weight === 'number' && Number.isFinite(weight) && weight > 0
    ? weight
    : null;
};

export interface CriterionMeta {
  readonly score: number | null;
  readonly weight: number | null;
}

export const getCriterionMeta = (
  input: AnalysisInput,
  criterion: string,
): CriterionMeta => ({
  score: getCriterionScore(input, criterion),
  weight: getCriterionWeight(input, criterion),
});

export const getCriteriaEntries = (
  input: AnalysisInput,
): Array<[string, CriterionBreakdown]> => {
  const criteria = normalizeAnalysisBreakdown(input)?.criteria;
  if (!criteria || typeof criteria !== 'object') return [];
  return Object.entries(criteria).filter(([, value]) => value && typeof value === 'object');
};

// True when the answer carries a weighted rubric (writing/speaking graded with
// task-type weights). In that case weight is the source of truth for which
// criteria are shown; criteria with no weight must not render. Answers without
// any weighted criteria (auto-scored/legacy/manual) return false so callers can
// fall back to showing whatever real feedback exists.
export const hasWeightedRubric = (input: AnalysisInput): boolean =>
  getCriteriaEntries(input).some(
    ([, criterion]) =>
      typeof criterion.weight === 'number' &&
      Number.isFinite(criterion.weight) &&
      criterion.weight > 0,
  );

export const getCriterionDisplayFeedback = (
  input: AnalysisInput,
  criterion: string,
  entry: CriterionBreakdown,
): string => {
  const breakdown = normalizeAnalysisBreakdown(input);
  if (!breakdown) return toText(entry.feedback);
  const enrichedEntry = breakdown.criteria?.[criterion];
  return toText(enrichedEntry?.feedback ?? entry.feedback);
};

export const hasAnalysisDetails = (input: AnalysisInput): boolean => {
  const breakdown = normalizeAnalysisBreakdown(input);
  if (!breakdown) return false;
  if (getCorrectedVersion(breakdown) || getFeedbackText(breakdown, 'task_fulfillment')) return true;
  if (getStrengths(breakdown).length || getImprovements(breakdown).length) return true;
  return (
    getCriteriaEntries(breakdown).length > 0 ||
    ([
      'task_fulfillment',
      'grammar',
      'spelling',
      'structure',
      'pronunciation',
      'fluency',
      'vocabulary',
      'register',
      'style',
      'interaction',
    ] as FeedbackKey[]).some((key) => getFeedbackText(breakdown, key))
  );
};

export const withAnalysisScoring = (
  input: AnalysisInput,
  points: number,
  isCorrect: boolean,
  source?: AnalysisBreakdown['source'],
): AnalysisBreakdown => {
  const score = Math.round(Math.max(0, Math.min(1, Number(points) || 0)) * 100);
  const existing = normalizeAnalysisBreakdown(input);
  const breakdown: AnalysisBreakdown = existing
    ? { ...existing }
    : {
        schema_version: ANALYSIS_BREAKDOWN_SCHEMA_VERSION,
        source: 'manual',
        rubric_version: ANALYSIS_RUBRIC_VERSION,
        task_type: null,
        criteria: {},
        feedback: {
          summary: '',
          strengths: [],
          improvements: [],
          corrected_version: '',
          task_fulfillment: { text: '' },
          grammar: { text: '', errors: [] },
          spelling: { text: '', typos: [] },
          structure: { text: '' },
          pronunciation: { text: '', errors: [] },
          fluency: { text: '', issues: [] },
          vocabulary: { text: '' },
          register: { text: '' },
          style: { text: '' },
          interaction: { text: '' },
        },
        transcription: { text: '', word_timestamps: [] },
        diagnostic_tags: [],
      };

  return enrichAnalysisBreakdown({
    ...breakdown,
    schema_version: breakdown.schema_version || ANALYSIS_BREAKDOWN_SCHEMA_VERSION,
    source: source ?? breakdown.source ?? 'ai',
    rubric_version: breakdown.rubric_version || ANALYSIS_RUBRIC_VERSION,
    final_score: score,
    points,
    is_correct: isCorrect,
    criteria: breakdown.criteria ?? {},
    feedback: breakdown.feedback ?? {},
    transcription: breakdown.transcription ?? { text: '', word_timestamps: [] },
    diagnostic_tags: breakdown.diagnostic_tags ?? [],
  });
};

export const withManualScoring = (
  input: AnalysisInput,
  points: number,
  isCorrect: boolean,
): AnalysisBreakdown => withAnalysisScoring(input, points, isCorrect, 'manual');

export const analysisBreakdownFromResult = (result: any): AnalysisBreakdown | null => {
  const returned = normalizeAnalysisBreakdown(result?.analysis_breakdown);
  if (returned) return returned;
  return null;
};
